// This is the entry point for the app. Basic config happens here - Redux

import React from "react";
import { Provider } from "react-redux";
import Amplify, { Auth, Hub } from "aws-amplify";
import { AUTH_TYPE, createAuthLink } from "aws-appsync-auth-link";
import { ConnectedRouter } from "connected-react-router";
import { createBrowserHistory } from "history";
import { dispatchTokenFailure } from "actions/fetch/fetch";
// import aws_exports from "aws-exports.js";

// Apollo
import { RetryLink } from "@apollo/client/link/retry";
import { ApolloClient, ApolloProvider, from, gql, HttpLink, InMemoryCache, split } from "@apollo/client";
import { onError } from "@apollo/client/link/error";

import localPolyfill from "config/localPolyfill.js";
import { addNotification, removeNotification } from "lib/toastNotification.js";
import initializeStore from "lib/initializeStore.js";
import { signOut } from "actions/login.js";

// Swap these to run offline
// import { SetupLoader } from "loaders";
import LoginController from "layout/LoginController";

// First LESS entry point
import "config/less/firstEntry.less";

export const history = createBrowserHistory();

localPolyfill();

// Enable extreme debugging
// window.LOG_LEVEL = "DEBUG";

export const store = initializeStore(history);

Amplify.configure({
	Auth: {
		// REQUIRED - Amazon Cognito Identity Pool ID
		identityPoolId: "us-east-1:facfe972-c13a-4dea-96d8-372f1ac49197",
		// REQUIRED - Amazon Cognito Region
		region: "us-east-1",
		// OPTIONAL - Amazon Cognito User Pool ID
		userPoolId: "us-east-1_CKdun6kyr",
		// OPTIONAL - Amazon Cognito Web Client ID
		userPoolWebClientId: "4qn94eh0gheufak1vuqvtrhpap",
		endpoint: "https://auth.exovera-projects.com"
		// "https://d6mmk1j47z7wl.cloudfront.net"
		// "https://auth.exovera-projects.com"
	},
	// Storage: {
	// 	AWSS3: {
	// 		bucket: "dos-brasillia-vod", // REQUIRED -  Amazon S3 bucket
	// 		region: "us-east-1", // OPTIONAL -  Amazon service region
	// 		// identityPoolId: "us-east-1:facfe972-c13a-4dea-96d8-372f1ac49197",
	// 	},
	// },
});

const listener = (data) => {
	// eslint-disable-next-line default-case
	switch (data.payload.event) {
	  case 'unauth':
		console.error("auth failed")
		store.dispatch(signOut());
		addNotification("Session Expired, please sign back in");
		setTimeout(() => {
			removeNotification();
		}, 10000);
		break;
	}
  };

Hub.listen(/.*/, listener)

const GRAPHQL_API_ENDPOINT_URL = process.env.GQL_URL;

	//  ------- Apollo 3.x --------
const retryLink = new RetryLink({
	delay: {
		initial: 1000,
		max: Infinity,
		jitter: true
	},
	attempts: {
		max: 5,
		retryIf: (error, _operation) => {
			const { loggedIn } = store.getState().login;
			const isAuthError = error && (error.statusCode === 401);
			const shouldRetry = loggedIn && (!isAuthError) && (!!error);
			if (shouldRetry) { 
				console.warn("Retrying GraphQL operation: ", _operation.operationName);
			}
			return shouldRetry;
		}
  }
});

const httpLink = new HttpLink({
	uri: GRAPHQL_API_ENDPOINT_URL,
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
	if (graphQLErrors)
		graphQLErrors.forEach(({ message, locations, path, errorType }) => {
			console.error(`[GraphQL error]: Message: ${message} (operation: ${operation.operationName})`)
			const { loggedIn } = store.getState().login;
			if (errorType && errorType === "UnauthorizedException" && loggedIn) {
				client.stop();
				store.dispatch(signOut());
				addNotification("Your session has expired, please sign back in");
				setTimeout(() => {
					removeNotification();
				}, 10000);
			}
		});
	if (networkError) {
		// console.warn(`[GraphQL network error]: ${networkError}`);
	}
});

const cache = new InMemoryCache();

const link = from([
	createAuthLink({
		url: GRAPHQL_API_ENDPOINT_URL,
		auth: {
			// type: AUTH_TYPE.API_KEY,
			// apiKey: SECRET_API_KEY,
			type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
			jwtToken: () => { return Auth.currentSession().then((data) => data.accessToken.jwtToken) },
		},
		region: "us-east-1",
	}),
	retryLink,
	errorLink, // Note - This should be after retry link to ensure retry attempts do not continue after forced logout
	httpLink
]);

const client = new ApolloClient({
	cache,
	link,
});

// AWS debug mode (super verbose)
// if (process.env.NODE_ENV !== "production") {
// 	window.LOG_LEVEL = "DEBUG";
// }

const App = () => {
	return (
		<ApolloProvider client={client}>
			<Provider store={store}>
				<ConnectedRouter history={history}>
					<LoginController apolloClient={client}/>
				</ConnectedRouter>
			</Provider>
		</ApolloProvider>
	);
};

export default App;
